import {useEffect, Fragment, useMemo} from 'react';
import dynamic from 'next/dynamic';
import Router from 'next/router';
import Head from 'next/head';
import {CacheProvider} from '@emotion/react';
import {Provider} from 'react-redux';
import {ThemeProvider} from '@jobseeker/lotus/helpers';
import {gaTrackPageView} from '@jobseeker/common-core/utils/gtag';
import {getBotGuardInstance} from '@jobseeker/common-core/utils/botGuard';
import {BotGuardCaptcha} from '@jobseeker/common-core/components';
import {BotCaptchaContextProvider} from '@jobseeker/common-core/contexts/BotCaptchaContext';
import cwvInpLogger from '@jobseeker/common-core/utils/cwvInpLogger';
import {
  getStoredReferrer,
  getStoredLandingUrl,
} from '@jobseeker/common-core/utils/referrer';

import {wrapper} from '@jobseeker/common-core/stores';
import {createEmotionCache} from '@helpers';
import '../styles/common.css';
import type {AppProps} from 'next/app';
import type {EmotionCache} from '@emotion/react';
import {APP_NAME_IIMJOBS, THEME} from '@constants';
import {metaInfo} from '@configs';

const StyledSnackbar = dynamic(
  () => import('@components/StyledSnackbar').then(mod => mod.default),
  {ssr: false}
);

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

const FeatureFlagInitializer = dynamic(
  () => import('@components/FeatureFlagInitializer'),
  {ssr: false}
);

interface WebAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

const APP_NAME = process.env.NEXT_PUBLIC_APP_NAME || APP_NAME_IIMJOBS;
const isAppIimjobs = APP_NAME === APP_NAME_IIMJOBS;

const WebApp = (props: WebAppProps) => {
  const {Component, emotionCache = clientSideEmotionCache, pageProps} = props;

  const metaData = useMemo(() => metaInfo[APP_NAME], []);
  const theme = useMemo(() => metaData[THEME], [metaData]);

  const {store} = wrapper.useWrappedStore(pageProps);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      gaTrackPageView(url, metaData['gaTagId']);
    };
    const handleRouteStart = (url: string) => {
      cwvInpLogger.report(url.split('?')[0]);
    };
    Router.events.on('routeChangeComplete', handleRouteChange);
    if (!isAppIimjobs) Router.events.on('routeChangeStart', handleRouteStart);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
      if (!isAppIimjobs)
        Router.events.off('routeChangeStart', handleRouteStart);
    };
  }, [metaData]);

  useEffect(() => {
    const loader = document.getElementById('globalLoader');
    if (loader) loader.style.display = 'none';
  }, []);

  useEffect(() => {
    // Eagerly capture referrer and landing URL on first render so OAuth
    // redirects (accounts.google.com etc.) can't overwrite the real source.
    getStoredReferrer();
    getStoredLandingUrl();
  }, []);

  useEffect(() => {
    if (isAppIimjobs) getBotGuardInstance()?.preloadEncryption();
    if (!isAppIimjobs) cwvInpLogger.start(window.location.pathname);
  }, []);

  return (
    <Fragment>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        ></meta>
      </Head>
      <CacheProvider value={emotionCache}>
        <Provider store={store}>
          <BotCaptchaContextProvider>
            <FeatureFlagInitializer />
            <ThemeProvider theme={theme}>
              <Component {...pageProps} />
              {isAppIimjobs && <BotGuardCaptcha />}
              <StyledSnackbar />
            </ThemeProvider>
          </BotCaptchaContextProvider>
        </Provider>
      </CacheProvider>
    </Fragment>
  );
};

export default WebApp;
